import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaEdit } from "react-icons/fa";
import { FaThumbsUp, FaShirt, FaEnvelope, FaQrcode } from "react-icons/fa6";
import { Dropdown } from 'react-bootstrap';
import axios from 'axios';
import useUserRoles from '../../hooks/useUserRoles';
import EditTeamModal from './EditTeamModal';
import InvitePlayerModal from './InvitePlayerModal';
import QRCodeModal from './QRCodeModal';

function TeamDetailPage() {
  const { teamId } = useParams();
  const [teamDetails, setTeamDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [games, setGames] = useState([]);
  const [user, setUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const { isCaptain } = useUserRoles();
  const [activeTab, setActiveTab] = useState("Schedule");

  useEffect(() => {
    // Fetch team details
    axios
      .get(`/api/teams/${teamId}/`, {
        withCredentials: true,
      })
      .then((response) => {
        setTeamDetails(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching team details:', error);
        setError('Failed to load team details.');
        setLoading(false);
      });
  }, [teamId]);

  useEffect(() => {
    // Fetch games for the active league
    axios.get(`/api/games/upcoming/`, {
      params: { is_played: false },
      withCredentials: true,
    })
      .then(response => {
        setGames(response.data);
      })
      .catch(error => {
        console.error('Error fetching games:', error);
      });
  }, []);

  useEffect(() => {
    axios.get('/api/users/my-details/', {
      withCredentials: true,
    })
      .then(response => {
        setUser(response.data);
      })
      .catch(error => {
        console.error('Error fetching user profile:', error);
      });
  }, []);

  const handleAttendingToggle = async (gameId, participantId, currentAttending) => {
    try {
      const response = await axios.patch(
        `/api/games/${gameId}/attending/`,
        { attending: !currentAttending },
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        setGames((prevGames) =>
          prevGames.map((game) =>
            game.id === gameId
              ? {
                ...game,
                participants: game.participants.map((p) =>
                  p.id === participantId ? { ...p, attending: !currentAttending } : p
                ),
              }
              : game
          )
        );
      }
    } catch (error) {
      console.error("Error updating attending status:", error);
    }
  };

  if (loading) {
    return <div className="container">Loading team details...</div>;
  }

  if (error) {
    return <div className="container text-danger">{error}</div>;
  }

  return (
    <>
      {/* Card START */}
      <div className="card mb-3 p-0">
        <div
          className="h-200px rounded-top"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/kb_static/Kickball_League_Banner.png)`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Card body START */}
        <div className="card-body py-0">
          <div className="text-center">
            <div className="avatar avatar-xxl mt-n5 mb-3">
              <img
                className="avatar-img rounded-circle border border-white border-3"
                src={teamDetails.logo}
                alt={`${teamDetails.name} logo`}
                style={{ backgroundColor: 'white' }}
                loading="lazy"
              />
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between pb-3">
            <div className="flex-grow-1 text-center">
              <h3 className="mb-0">
                {teamDetails.name}
              </h3>
            </div>
            <div className="d-flex align-items-center">
              {isCaptain && (
                <Dropdown>
                  <Dropdown.Toggle bsPrefix="dropdown-icon btn-sm" variant="light" id="profileAction2">
                    <i className="bi bi-three-dots"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setShowEditModal(true)}>
                      <FaEdit className="me-1" /> Edit Team
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowInviteModal(true)}>
                      <FaEnvelope className="me-1" /> Invite Players
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowQRModal(true)}>
                      <FaQrcode className="me-1" /> QR Code
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
        </div>
      </div>

      <EditTeamModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        team={teamDetails}
        onTeamUpdated={(updatedTeam) => {
          setTeamDetails(updatedTeam);
          setShowEditModal(false);
        }}
      />

      <InvitePlayerModal
        show={showInviteModal}
        handleClose={() => setShowInviteModal(false)}
        team={teamDetails}
        season={teamDetails?.current_season}
      />

      <QRCodeModal
        show={showQRModal}
        handleClose={() => setShowQRModal(false)}
        team={teamDetails}
        season={teamDetails?.current_season}
      />

      <div className="card card-body mb-3">
        <div className="row g-4">
          <div className="col-6 col-lg-4">
            <h5>Captain</h5>
            <div className="d-flex align-items-center">
              <span className="small m-2">
                {teamDetails.captain_name || 'No Captain'}
              </span>
              <img
                className="avatar-img rounded-circle me-2"
                src={
                  teamDetails.captain_picture
                    ? teamDetails.captain_picture
                    : `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`
                }
                alt={
                  teamDetails.captain_name
                    ? `${teamDetails.captain_name} avatar`
                    : 'Default avatar'
                }
                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                loading="lazy"
              />
            </div>
          </div>
          <div className="col-6 col-lg-4">
            <h5>
              <span className="d-md-none">A-Captain</span>
              <span className="d-none d-md-inline">Assistant Captain</span>
            </h5>
            <div className="d-flex align-items-center">
              <span className="small m-2">
                {teamDetails.a_cap_name || 'None'}
              </span>
              <img
                className="avatar-img rounded-circle me-2"
                src={
                  teamDetails.a_cap_picture
                    ? teamDetails.a_cap_picture
                    : `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`
                }
                alt={
                  teamDetails.a_cap_name
                    ? `${teamDetails.a_cap_name} avatar`
                    : 'Default avatar'
                }
                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                loading="lazy"
              />
            </div>
          </div>
          <div className="col-sm-6 col-lg-4">
            <h5>Team Color</h5>
            <p className="small mb-0"><FaShirt style={{ fontSize: '20px', color: teamDetails.color }}></FaShirt> {teamDetails.color_name}</p>
          </div>
        </div>
        <hr className="mt-4" />
        <div className="row align-items-center">
          <div className="col-lg-6">
            <h5>Players</h5>
            <ul className="avatar-group list-unstyled align-items-center">
              {teamDetails.players
                .slice(0, 5)
                .map((p, idx) => (
                  <li key={`${p.id}-${idx}`} className="avatar avatar-xs">
                    <img
                      className="avatar-img rounded-circle"
                      src={
                        p.profile_picture ||
                        `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`
                      }
                      alt={`${p.first_name} ${p.last_name}`}
                      loading="lazy"
                    />
                  </li>
                ))
              }

              <li className="ms-4">
                <small>
                  {teamDetails.players.length}{' '}
                  {teamDetails.players.length === 1 ? 'team member' : 'team members'}
                </small>
              </li>
            </ul>
          </div>
          <div className="col-lg-6">
            <div className="row g-2">
              <div className="col-4">
                <div className="d-flex">
                  <i className="bi bi-trophy fs-4"></i>
                  <div className="ms-3">
                    <h5 className="mb-0">{teamDetails.wins}</h5>
                    <p className="mb-0">Wins</p>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="d-flex">
                  <i className="bi bi-trophy-fill fs-4"></i>
                  <div className="ms-3">
                    <h5 className="mb-0">{teamDetails.losses}</h5>
                    <p className="mb-0">Losses</p>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="d-flex">
                  <i className="bi bi-trophy-fill fs-4"></i>
                  <div className="ms-3">
                    <h5 className="mb-0">{teamDetails.draws}</h5>
                    <p className="mb-0">Draws</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="card">
        <div className="card-header border-0 pb-0">
          <ul className="nav nav-tabs nav-bottom-line justify-content-center justify-content-md-start">
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "Schedule" ? "active" : ""}`}
                href="#schedule"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("Schedule");
                }}
              >
                Schedule
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${activeTab === "Players" ? "active" : ""}`}
                href="#players"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("Players");
                }}
              >
                Players
              </a>
            </li>
          </ul>
        </div>

        {/* Tab Content */}
        <div className="card-body">
          <div className="tab-content">
            {/* Schedule Tab */}
            <div className={`tab-pane fade ${activeTab === "Schedule" ? "show active" : ""}`} id="schedule">
              <div className="row g-4">
                {games.length > 0 ? (
                  games.map((game) => {
                    const homeParticipant = game.participants?.find((p) => p.is_home && p.content_type === 'team') || {};
                    const awayParticipant = game.participants?.find((p) => p.is_away && p.content_type === 'team') || {};

                    const userParticipant = game.participants?.find(
                      (p) => p.content_type === "player" && user?.players?.some(player => player.id === p.object_id)
                    ) || {};

                    return (
                      <div key={game.id} className="col-12">
                        <div className="row align-items-center">
                          <div className="col-6 col-md-auto order-1 d-flex align-items-center">
                            <div className="avatar avatar-xl d-flex align-items-center">
                              <div className="position-relative">
                                <div
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    margin: 'auto',
                                    display: 'block',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: '8px',
                                  }}
                                >
                                  {homeParticipant.participant_logo ? (
                                    <img
                                      src={homeParticipant.participant_logo}
                                      alt={`${homeParticipant.participant_name} logo`}
                                      style={{
                                        width: '50px',
                                        height: '50px',
                                        objectFit: 'cover',
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
                                      }}
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '50%',
                                        backgroundColor: 'blue',
                                        position: 'absolute',
                                        top: 0,
                                      }}
                                    ></div>
                                  )}

                                  {awayParticipant.participant_logo ? (
                                    <img
                                      src={awayParticipant.participant_logo}
                                      alt={`${awayParticipant.participant_name} logo`}
                                      style={{
                                        width: '50px',
                                        height: '50px',
                                        objectFit: 'cover',
                                        position: 'absolute',
                                        left: 0,
                                        bottom: 0,
                                        clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
                                      }}
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '50%',
                                        backgroundColor: 'red',
                                        position: 'absolute',
                                        bottom: 0,
                                      }}
                                    ></div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-md order-3 order-md-2">
                            <h5 className="mb-1">
                              <Link className="nav-link" to={`/games/${game.id}`}>
                                {game.home_team} vs. {game.away_team}
                              </Link>
                            </h5>
                            <ul className="nav nav-stack small">
                              <li className="nav-item">
                                <i className="bi bi-calendar-check pe-1"></i> {game.scheduled_date_only} at {game.scheduled_time_only}
                              </li>
                              <li className="nav-item">
                                <i className="bi bi-geo-alt pe-1"></i> {game.location.name}
                              </li>
                            </ul>
                          </div>
                          <div className="col-6 col-md-auto order-2 order-md-3 d-flex align-items-center justify-content-end">
                            <input
                              type="checkbox"
                              className="btn-check"
                              id={`Interested-${userParticipant.id}`}
                              checked={userParticipant.attending || false}
                              onChange={() => handleAttendingToggle(game.id, userParticipant.id, userParticipant.attending)}
                            />
                            <label
                              className={`btn btn-sm ${userParticipant.attending ? 'btn-success' : 'btn-outline-success'} text-nowrap`}
                              htmlFor={`Interested-${userParticipant.id}`}
                              style={{ whiteSpace: 'nowrap', maxWidth: '100%' }}
                            >
                              <FaThumbsUp className="me-1" /> {userParticipant.attending ? "Attending" : "Not Attending"}
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p>No upcoming games found.</p>
                )}
              </div>
            </div>

            {/* Players Tab */}
            <div className={`tab-pane fade ${activeTab === "Players" ? "show active" : ""}`} id="players">
              <div className="row">
                {teamDetails.players?.map((player) => (
                  <div key={player.id} className="col-6 col-sm-12 d-flex flex-wrap align-items-center mb-3">
                    <div className="avatar avatar-md">
                      <img
                        className="avatar-img rounded-circle"
                        src={
                          player.profile_picture ||
                          `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`
                        }
                        alt={`${player.first_name} ${player.last_name}`}
                      />
                    </div>
                    <div className="ms-sm-2 my-2 my-sm-0">
                      <h6 className="mb-0">{player.first_name} {player.last_name}</h6>
                      <p className="small mb-0">
                        {teamDetails.captain === player.id ? 'Captain' :
                          teamDetails.a_cap === player.id ? 'Assistant Captain' :
                            'Player'}
                      </p>
                      <div className="small text-muted">
                        {player.email && (
                          <p className="mb-1">
                            <i className="bi bi-envelope me-1"></i>
                            <a href={`mailto:${player.email}`} className="text-decoration-none">{player.email}</a>
                          </p>
                        )}
                        {player.phone_number && (
                          <p className="mb-1">
                            <i className="bi bi-telephone me-1"></i>
                            <a href={`tel:${player.phone_number}`} className="text-decoration-none">{player.phone_number}</a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TeamDetailPage;
